import React, { useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from "@mui/material";
import SwitchField from "@/components/editable/EditableField/fields/SwitchField";
import { useTranslation } from "react-i18next";
import { theme } from "../../theme";
import { SessionContext } from "@/utility/useSession";
import {
  type PickupLocationResponse,
  PickupLocationEntityType
} from "@/logic/api";

interface AddEditPickupLocationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    name: string;
    latitude: number | null;
    longitude: number | null;
    isActive: boolean;
    matchingEntityType: PickupLocationEntityType | null;
    matchingEntityReference: string | null;
  }) => Promise<void>;
  location?: PickupLocationResponse;
  isLoading?: boolean;
}

type EntityTypeOption = PickupLocationEntityType | "";

interface FormState {
  name: string;
  longitude: string;
  latitude: string;
  isActive: boolean;
  matchingEntityType: EntityTypeOption;
  matchingEntityReference: string;
}

interface FormErrors {
  name?: string;
  latitude?: string;
  longitude?: string;
  coordinates?: string;
}

const buildInitialFormState = (location?: PickupLocationResponse): FormState => ({
  name: location?.name ?? "",
  longitude: location?.coordinates?.longitude?.toString() ?? "",
  latitude: location?.coordinates?.latitude?.toString() ?? "",
  isActive: location?.isActive ?? true,
  matchingEntityType: location?.matchingEntity?.matchingEntityType ?? "",
  matchingEntityReference: location?.matchingEntity?.matchingEntityReference ?? ""
});

export const AddEditPickupLocationModal: React.FC<AddEditPickupLocationModalProps> = ({
  open,
  onClose,
  onSave,
  location,
  isLoading = false
}) => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);

  const [formData, setFormData] = useState<FormState>(() => buildInitialFormState(location));
  const [errors, setErrors] = useState<FormErrors>({});
  const [saveError, setSaveError] = useState<string>("");

  const isEditMode = Boolean(location);

  // Use terminals already available in the session — no API call needed
  const sessionTerminals = (session.current.site?.terminals ?? []).map(ta => ta.terminal);
  const sessionCarParks = session.current.site?.carParks ?? [];

  const hasChanges = !isEditMode || (
    formData.name !== (location?.name ?? "") ||
    formData.longitude !== (location?.coordinates?.longitude?.toString() ?? "") ||
    formData.latitude !== (location?.coordinates?.latitude?.toString() ?? "") ||
    formData.isActive !== location?.isActive ||
    (formData.matchingEntityType === "" ? null : formData.matchingEntityType) !== (location?.matchingEntity?.matchingEntityType ?? null) ||
    formData.matchingEntityReference !== (location?.matchingEntity?.matchingEntityReference ?? "")
  );

  const handleFieldChange = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleEntityTypeChange = (value: EntityTypeOption) => {
    setFormData(prev => ({
      ...prev,
      matchingEntityType: value,
      matchingEntityReference: ""
    }));
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t("settings.shuttlePickup.pickupLocations.errors.nameRequired");
    }

    const hasLat = formData.latitude.trim() !== "";
    const hasLng = formData.longitude.trim() !== "";

    if (hasLat !== hasLng) {
      newErrors.coordinates = t("settings.shuttlePickup.pickupLocations.errors.coordinatesBothRequired");
    }

    if (hasLat) {
      const lat = parseFloat(formData.latitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        newErrors.latitude = t("settings.shuttlePickup.pickupLocations.errors.latitudeInvalid");
      }
    }

    if (hasLng) {
      const lng = parseFloat(formData.longitude);
      if (isNaN(lng) || lng < -180 || lng > 180) {
        newErrors.longitude = t("settings.shuttlePickup.pickupLocations.errors.longitudeInvalid");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setSaveError("");
      const lat = formData.latitude.trim() ? parseFloat(formData.latitude) : null;
      const lng = formData.longitude.trim() ? parseFloat(formData.longitude) : null;
      const entityType = formData.matchingEntityType === "" ? null : formData.matchingEntityType;
      const entityRef = formData.matchingEntityReference.trim() || null;

      await onSave({
        name: formData.name.trim(),
        latitude: lat,
        longitude: lng,
        isActive: formData.isActive,
        matchingEntityType: entityType,
        matchingEntityReference: entityRef
      });
      onClose();
    } catch (error) {
      console.error("Error saving pickup location:", error);
      setSaveError(
        error instanceof Error
          ? error.message
          : t("settings.shuttlePickup.pickupLocations.errors.saveFailed")
      );
    }
  };

  const showAssetLocationInput = formData.matchingEntityType === PickupLocationEntityType.AssetLocation;
  const showTerminalDropdown = formData.matchingEntityType === PickupLocationEntityType.Terminal;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      data-testid="add-edit-pickup-location-modal"
    >
      <DialogTitle>
        <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
          {isEditMode
            ? t("settings.shuttlePickup.pickupLocations.editLocation")
            : t("settings.shuttlePickup.pickupLocations.addNewLocation")}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

          {/* Location Name */}
          <Box>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
              {t("settings.shuttlePickup.pickupLocations.fields.name")}
              {" "}<span style={{ color: theme.palette.error.main }}>*</span>
            </Typography>
            <TextField
              fullWidth
              value={formData.name}
              onChange={(e) => handleFieldChange("name", e.target.value)}
              error={Boolean(errors.name)}
              helperText={errors.name}
              disabled={isLoading}
              size="small"
              variant="outlined"
              autoComplete="off"
              data-testid="pickup-location-name-input"
            />
          </Box>

          {/* Longitude / Latitude side by side */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                {t("settings.shuttlePickup.pickupLocations.fields.latitude")}
              </Typography>
              <TextField
                fullWidth
                value={formData.latitude}
                onChange={(e) => handleFieldChange("latitude", e.target.value)}
                error={Boolean(errors.latitude || errors.coordinates)}
                helperText={errors.latitude || t("settings.shuttlePickup.pickupLocations.fields.latitudeHint")}
                disabled={isLoading}
                size="small"
                variant="outlined"
                autoComplete="off"
                slotProps={{ input: { inputMode: "decimal" } }}
                data-testid="pickup-location-latitude-input"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                {t("settings.shuttlePickup.pickupLocations.fields.longitude")}
              </Typography>
              <TextField
                fullWidth
                value={formData.longitude}
                onChange={(e) => handleFieldChange("longitude", e.target.value)}
                error={Boolean(errors.longitude || errors.coordinates)}
                helperText={errors.longitude || t("settings.shuttlePickup.pickupLocations.fields.longitudeHint")}
                disabled={isLoading}
                size="small"
                variant="outlined"
                autoComplete="off"
                slotProps={{ input: { inputMode: "decimal" } }}
                data-testid="pickup-location-longitude-input"
              />
            </Box>
          </Box>
          {errors.coordinates && (
            <Typography variant="caption" color="error" sx={{ mt: -1.5 }}>
              {errors.coordinates}
            </Typography>
          )}

          {/* Pickup Trigger — association type */}
          <Box>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
              {t("settings.shuttlePickup.pickupLocations.fields.pickupTrigger")}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="matching-entity-type-label">
                {t("settings.shuttlePickup.pickupLocations.fields.matchingEntityType")}
              </InputLabel>
              <Select
                labelId="matching-entity-type-label"
                value={formData.matchingEntityType}
                onChange={(e) => handleEntityTypeChange(e.target.value as EntityTypeOption)}
                label={t("settings.shuttlePickup.pickupLocations.fields.matchingEntityType")}
                disabled={isLoading}
                data-testid="pickup-location-entity-type-select"
              >
                <MenuItem value="">
                  <em>{t("settings.shuttlePickup.pickupLocations.fields.noAssociation")}</em>
                </MenuItem>
                <MenuItem value={PickupLocationEntityType.AssetLocation} data-testid="entity-type-asset-location">
                  {t("settings.shuttlePickup.pickupLocations.entityType.assetLocation")}
                </MenuItem>
                <MenuItem value={PickupLocationEntityType.Terminal} data-testid="entity-type-terminal">
                  {t("settings.shuttlePickup.pickupLocations.entityType.terminal")}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Car Park Location  */}
          {showAssetLocationInput && (
            <Box>
              <FormControl fullWidth size="small">
                <InputLabel id="carpark-reference-label">
                  {t("settings.shuttlePickup.pickupLocations.fields.assetLocationReference")}
                </InputLabel>
                <Select
                  labelId="carpark-reference-label"
                  value={formData.matchingEntityReference}
                  onChange={(e) => handleFieldChange("matchingEntityReference", e.target.value)}
                  label={t("settings.shuttlePickup.pickupLocations.fields.assetLocationReference")}
                  disabled={isLoading}
                  data-testid="pickup-location-carpark-select"
                >
                  <MenuItem value="">
                    <em>{t("settings.shuttlePickup.pickupLocations.fields.selectCarPark")}</em>
                  </MenuItem>
                  {sessionCarParks.map((carPark) => (
                    <MenuItem
                      key={carPark.uuid}
                      value={carPark.uuid}
                      data-testid={`carpark-option-${carPark.code}`}
                    >
                      {carPark.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Terminal — dropdown from session terminals */}
          {showTerminalDropdown && (
            <Box>
              <FormControl fullWidth size="small">
                <InputLabel id="terminal-reference-label">
                  {t("settings.shuttlePickup.pickupLocations.fields.terminalReference")}
                </InputLabel>
                <Select
                  labelId="terminal-reference-label"
                  value={formData.matchingEntityReference}
                  onChange={(e) => handleFieldChange("matchingEntityReference", e.target.value)}
                  label={t("settings.shuttlePickup.pickupLocations.fields.terminalReference")}
                  disabled={isLoading}
                  data-testid="pickup-location-terminal-select"
                >
                  <MenuItem value="">
                    <em>{t("settings.shuttlePickup.pickupLocations.fields.selectTerminal")}</em>
                  </MenuItem>
                  {sessionTerminals.map((terminal) => (
                    <MenuItem
                      key={terminal.uuid}
                      value={terminal.uuid}
                      data-testid={`terminal-option-${terminal.uuid}`}
                    >
                      {terminal.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Active / Inactive toggle */}
          <SwitchField
            label={t("settings.shuttlePickup.pickupLocations.fields.isActive")}
            value={formData.isActive}
            onChange={(val) => handleFieldChange("isActive", val)}
            readOnly={isLoading}
            testId="pickup-location-active-switch"
            compact
          />

        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          variant="text"
          disabled={isLoading}
          sx={{
            transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
            "&:active": { transform: "scale(0.97)" },
            "&:hover": { backgroundColor: (theme) => theme.palette.action.hover },
            "&:focus-visible": { boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40` },
          }}
          disableRipple
          disableElevation
          data-testid="cancel-pickup-location-button"
        >
          {t("brand.actions.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading || (isEditMode && !hasChanges)}
          sx={{
            fontWeight: "bold",
            backgroundColor: (theme) => theme.palette.brand.main,
            color: (theme) => theme.palette.primary.contrastText,
            transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
            "&:hover": {
              backgroundColor: (theme) => theme.palette.brand.dark,
              color: (theme) => theme.palette.primary.contrastText,
            },
            "&:active": { transform: "scale(0.97)" },
            "&:focus-visible": { boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40` },
            "&:disabled": { backgroundColor: "#ccc", color: "#666" }
          }}
          disableRipple
          disableElevation
          data-testid="save-pickup-location-button"
        >
          {isLoading
            ? t("brand.actions.saving")
            : isEditMode
              ? t("settings.shuttlePickup.pickupLocations.editLocation")
              : t("settings.shuttlePickup.pickupLocations.addNewLocation")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
